import {
  BatchDetailTabs,
  DeliveryLocationStatus,
  GroupedAssignedItems,
  OrderItemStatus,
  OrderItems,
  ReceiptBypassMode,
  RunForBatchDetail,
  itemsForSection,
  itemsSortBy,
  UPCOrderItem as upcOrderItem,
} from '../../../batchDetails/types';
import {Box, HBox} from '../../../components/core/view';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Divider,
  IconButton,
  Skeleton,
} from 'native-base';
import {HomeScreens, ShopperScreens} from '../../../home/navigation/types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  batchDetailActionsLocal,
  useBatchDetailContext,
} from '../../../batchDetails/hooks/batchDetail';
import {
  useAdjacentOOSItems,
  useGetOrdersForOrderAssignee,
  useUPCShift,
  useUpdateOrderItemStatus,
} from '../../../batchDetails/hooks/BatchDetails';
import {useColorThemeType, useFontTheme} from '../../../appStyles/hooks/theme';
import {
  useGetItemStatusCount,
  useSetDeliveryLocationStatusOfAssignedOrders,
  useUPCOrder,
} from '../../../batchDetails/hooks/OrderOperation';
import {useIsFocused, useRoute} from '@react-navigation/native';

import {Accordian} from '../../../components/composites';
import AddToCartIcon from '../../../components/core/icon/AddToCartIcon';
import {Analytics} from '../../../config/analytics/analytics';
import {BottomButton} from '../../../components/composites/Button';
import {CustomFlatList} from '../../../components/core/list';
import CustomModal from '@buncha/common/components/CustomModal/CustomModal';
import {CustomText} from '../../../components/core/text';
import CustomerOrderInfo from '../CustomerOrderInfo';
import DoneOrderItemList from '../DoneOrderItemList';
import EmptyDoneSection from '../EmptyDoneSection';
import {EventType} from '../../../config/analytics/type';
import If from '../../../../src/components/conditional/If';
import {MainScreens} from '@buncha/navigation/types';
import {Modules} from '@buncha/utils/types';
import {ReceiptModal} from '../RecieptModal';
import {SpinnerComponent} from '../../../components/core/loader';
import UPCOrderItem from '../BatchOrderItem/UPCOrderItem';
import {getClientType} from '@buncha/common/types/common';
import listener from '@buncha/config/listener';
import {navigation} from '../../../config/navigation';
import {smartLook} from '@buncha/config/analytics';
import {styles} from './styles';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';

interface DoneItemListProps {
  runDetail?: RunForBatchDetail;
  isUPCMode?: boolean;
  upcOrderId: number;
  navigateToReceiptScreen: () => void;
  navigateToAddItem: (item: GroupedAssignedItems) => void;
}

function DoneItemList(props: DoneItemListProps) {
  const route = useRoute();
  const routeParams: {runId?: number} | undefined = route?.params;
  const runId = Number(routeParams?.runId);
  const {state, dispatch} = useBatchDetailContext();
  const {button} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};
  const [getOrdersForOrderAssignee, loading] =
    useGetOrdersForOrderAssignee(dispatch);
  const theme = useColorThemeType();
  const muliFont = useFontTheme();
  const {updateOrderItemStatus, changeStatusState} = useUpdateOrderItemStatus();
  const [setDeliveryLocationStatusOfAssignedOrders, pickState] =
    useSetDeliveryLocationStatusOfAssignedOrders(dispatch);
  const [getItemStatusCount, countState] = useGetItemStatusCount(dispatch);
  const {getUPCShiftOrder, orderLoading} = useUPCShift(dispatch);
  const {updateOrderStatus} = useUPCOrder();
  const {getAdjacentOOSItems} = useAdjacentOOSItems(dispatch);

  const {selectedCatalogStoreForUPCRun} = useAppSelector(
    gState => gState.itemScanner,
  );

  const {
    navigateToReceiptScreen,
    runDetail,
    isUPCMode,
    upcOrderId,
    navigateToAddItem,
  } = props;

  const isFocused = useIsFocused();
  const [showModal, setShowModal] = useState(false);
  const [uploadReceipt, setUploadReceipt] = useState(true);
  const storeId = runDetail?.store?.id ?? 0;
  const isMeijerStore = useIsMeijerStore(storeId);

  const tutorial = state.tutorial;

  const isLoading = Boolean(
    pickState.loading ||
      loading ||
      countState.loading ||
      changeStatusState.loading,
  );

  const buttonTitle = useMemo(() => {
    if (uploadReceipt && !isUPCMode && !isMeijerStore)
      return button?.submitReceipt;
    return button?.donePicking;
  }, [
    button?.donePicking,
    button?.submitReceipt,
    isMeijerStore,
    isUPCMode,
    uploadReceipt,
  ]);

  useEffect(() => {
    if (state?.tabDataCount?.doneCount)
      Analytics.eventWithProps('Done Tab', {
        type: EventType.cta,
        runId: runId,
        totalItem: state?.tabDataCount?.doneCount || 0,
        client: getClientType(isMeijerStore),
      });

    return () => {
      if (dispatch)
        dispatch(batchDetailActionsLocal.setDisablePickingRadioButton(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.tabDataCount?.doneCount]);

  useEffect(() => {
    const unsub = listener.addListener('chatCount', (count: number) => {
      dispatch?.(batchDetailActionsLocal.setUnreadCount(count));
    });

    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      state.hasSkipReceipt === ReceiptBypassMode.skipByShopper &&
      !state.unreadCount
    )
      setUploadReceipt(false);
    else setUploadReceipt(true);
  }, [state.hasSkipReceipt, state.unreadCount]);

  useEffect(() => {
    if (!dispatch) return;
    if (runId && !runDetail?.receiptImageUrls?.length) {
      setUploadReceipt(true);
      dispatch(batchDetailActionsLocal.setOpenUploadReceiptModal(true));
      dispatch(
        batchDetailActionsLocal.setSkippedReceiptFlow(ReceiptBypassMode.none),
      );
    }
  }, [dispatch, runDetail?.receiptImageUrls?.length, runId]);

  const buttonDisabled = useMemo(() => {
    if (isUPCMode)
      return !(
        state?.tabDataCount?.todoCount === 0 &&
        state?.tabDataCount?.doneCount >= 0
      );

    return Boolean(
      state.remainingItemCount[0] ||
        isLoading ||
        state.tabDataCount.todoCount ||
        state.groupedAssignedItems.locate?.length ||
        state.groupedAssignedItems.sent?.length,
    );
  }, [
    isLoading,
    isUPCMode,
    state.groupedAssignedItems.locate?.length,
    state.groupedAssignedItems.sent?.length,
    state.remainingItemCount,
    state.tabDataCount?.doneCount,
    state.tabDataCount.todoCount,
  ]);

  const navigateToOrderPack = useCallback(() => {
    const eventParams = {
      type: EventType.cta,
      runId: runId,
      deliveryLocationStatus: DeliveryLocationStatus.readyToPack,
      client: getClientType(isMeijerStore),
    };
    Analytics.eventWithProps('Start of Packing Flow (Shopper)', eventParams);
    smartLook.trackCustomEvent('Start of Packing Flow (Shopper)', eventParams);
    navigation.navigate(HomeScreens.Shopper, {
      screen: ShopperScreens.OrderPackingPage,
      params: {runId: runId, tutorial: tutorial ? 'true' : undefined},
    });
  }, [isMeijerStore, runId, tutorial]);

  const updateUPCOrdetItemStatus = useCallback(async () => {
    await updateOrderStatus(selectedCatalogStoreForUPCRun?.id ?? 0, () => {
      navigation.replace(MainScreens.Home, {
        screen: HomeScreens.UPCShifts,
      });
    });
  }, [selectedCatalogStoreForUPCRun?.id, updateOrderStatus]);

  const orderPickState = useCallback(async () => {
    if (isUPCMode) {
      updateUPCOrdetItemStatus();
    } else if (uploadReceipt && !isMeijerStore) {
      navigateToReceiptScreen();
    } else if (runId) {
      Analytics.eventWithProps('Done Picking', {
        type: EventType.cta,
        runId: runId,
        deliveryLocationStatus: DeliveryLocationStatus.readyToPack,
        client: getClientType(isMeijerStore),
      });
      await setDeliveryLocationStatusOfAssignedOrders(
        runId,
        DeliveryLocationStatus.readyToPack,
        state.orderIdsToBeSend || [],
        navigateToOrderPack,
      );
    }
  }, [
    uploadReceipt,
    isMeijerStore,
    runId,
    updateUPCOrdetItemStatus,
    isUPCMode,
    navigateToReceiptScreen,
    setDeliveryLocationStatusOfAssignedOrders,
    state.orderIdsToBeSend,
    navigateToOrderPack,
  ]);

  const getDoneItem = useCallback(() => {
    if (runId) {
      getItemStatusCount(runId, [OrderItemStatus.notPicked]);
      getOrdersForOrderAssignee(
        runId,
        itemsForSection.done,
        itemsSortBy.customer,
      );
    }
  }, [getItemStatusCount, getOrdersForOrderAssignee, runId]);

  const changedItemStatus = useCallback(
    (orderItem: OrderItems) => {
      Analytics.eventWithProps('Uncheck Item', {
        type: EventType.cta,
        runID: runId,
        itemStatus: OrderItemStatus.notPicked,
        currentItemStatus: orderItem?.itemStatus,
        orderItemId: orderItem.id,
        client: getClientType(isMeijerStore),
      });
      if (runId)
        updateOrderItemStatus(
          runId,
          OrderItemStatus.notPicked,
          [orderItem.id],
          getDoneItem,
        );
    },
    [getDoneItem, isMeijerStore, runId, updateOrderItemStatus],
  );

  const handleAddItem = useCallback(
    (item: GroupedAssignedItems) => {
      const orderId = item.orderItems?.length ? item.orderItems[0].orderId : 0;

      Analytics.eventWithProps('Addition Item', {
        type: EventType.cta,
        runId: runId,
        client: getClientType(isMeijerStore),
        orderId,
        source: 'Done Tab',
      });
      navigateToAddItem(item);
    },
    [isMeijerStore, navigateToAddItem, runId],
  );

  const renderItem = useCallback(
    ({item}: {item: GroupedAssignedItems | upcOrderItem; index: number}) => {
      if (isUPCMode && !orderLoading) {
        const updatedItem = item as upcOrderItem;
        return (
          <Box style={[styles.mainContainer]}>
            <UPCOrderItem orderItem={updatedItem} />
          </Box>
        );
      }

      if (isUPCMode && orderLoading)
        return (
          <Box style={styles.skeletonContainer}>
            <Skeleton marginTop={5} height={75} color={'red'} />
          </Box>
        );

      const itemForRender = item as GroupedAssignedItems;
      const isVip = itemForRender?.metaData?.buyer?.isVip;

      return (
        <Box style={[styles.mainContainer]}>
          <Accordian
            isExpanded={true}
            header={
              <HBox style={[styles.mainHeaderContainer]}>
                <CustomerOrderInfo
                  groupedAssignedItems={itemForRender}
                  isVip={isVip}
                  adjShopperDetails={itemForRender?.metaData?.firstShopperName}
                />
                <HBox>
                  <If condition={!itemForRender?.metaData?.firstShopperName}>
                    <IconButton onPress={() => handleAddItem(itemForRender)}>
                      <AddToCartIcon
                        width={18}
                        height={18}
                        viewBox="0 0 21 21"
                      />
                    </IconButton>
                  </If>
                  <Box
                    style={[
                      styles.countBox,
                      {
                        backgroundColor: theme?.background.gray[200],
                      },
                    ]}>
                    <CustomText
                      style={[
                        {
                          fontFamily: muliFont[700].normal,
                          color: theme?.text.gray[100],
                        },
                      ]}>
                      {itemForRender?.orderItems?.length}
                    </CustomText>
                  </Box>
                </HBox>
              </HBox>
            }
            iconEnable={<ChevronUpIcon color={theme?.text.blue[300]} />}
            iconDisable={<ChevronDownIcon color={theme?.text.blue[300]} />}>
            <DoneOrderItemList
              runId={runDetail?.id || 0}
              esIndex={runDetail?.store?.elasticSearchIndex ?? ''}
              storeId={runDetail?.store?.id}
              orderItems={itemForRender.orderItems}
              isSecondShopper={Boolean(
                itemForRender?.metaData?.firstShopperName,
              )}
              onClick={changedItemStatus}
              disableOnclick={
                pickState.loading ||
                changeStatusState.loading ||
                state?.disablePickingRadioButton ||
                false
              }
            />
          </Accordian>
        </Box>
      );
    },
    [
      isUPCMode,
      orderLoading,
      theme?.background.gray,
      theme?.text.gray,
      theme?.text.blue,
      muliFont,
      runDetail?.id,
      runDetail?.store?.elasticSearchIndex,
      runDetail?.store?.id,
      changedItemStatus,
      pickState.loading,
      changeStatusState.loading,
      state?.disablePickingRadioButton,
      handleAddItem,
    ],
  );

  const Separator = useCallback(() => <Divider style={styles.divider} />, []);

  useEffect(() => {
    if (dispatch) dispatch(batchDetailActionsLocal.setShowSortBarValue(false));
  }, [dispatch]);

  useEffect(() => {
    if (runId && isFocused && !isUPCMode)
      getOrdersForOrderAssignee(
        runId,
        itemsForSection.done,
        itemsSortBy.customer,
      );
    else if (isUPCMode)
      getUPCShiftOrder(upcOrderId, BatchDetailTabs.done, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, isFocused, isUPCMode]);

  useEffect(() => {
    if (runId) getItemStatusCount(runId, [OrderItemStatus.notPicked]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const dataToRender = useMemo(() => {
    if (isUPCMode) return state.upcOrderData?.orderItems;
    return state.groupedAssignedItems.done;
  }, [
    isUPCMode,
    state.groupedAssignedItems.done,
    state.upcOrderData?.orderItems,
  ]);

  const handleOnPressReceiptModal = useCallback(() => {
    if (dispatch) {
      setShowModal(false);
      setUploadReceipt(true);
      dispatch(batchDetailActionsLocal.setOpenUploadReceiptModal(true));
    }
  }, [dispatch]);

  useEffect(() => {
    getAdjacentOOSItems('sent', runId);
    getAdjacentOOSItems('locate', runId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <CustomFlatList
        data={dataToRender}
        renderItem={renderItem}
        ItemSeparatorComponent={Separator}
        loading={orderLoading || loading}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          <If condition={!(orderLoading || loading)}>
            <EmptyDoneSection />
          </If>
        }
        loadingSkeleton={
          <Box style={styles.skeletonContainer}>
            <Skeleton marginTop={5} height={75} />
          </Box>
        }
        style={[styles.scrollContainer]}
        contentContainerStyle={styles.contentBox}
      />
      <If condition={showModal}>
        <CustomModal open={showModal} onClose={handleOnPressReceiptModal}>
          <ReceiptModal onClose={handleOnPressReceiptModal} />
        </CustomModal>
      </If>
      <BottomButton
        title={buttonTitle}
        disabled={buttonDisabled}
        onPress={orderPickState}
        isLoading={orderLoading || loading}
        loadingIndicator={<SpinnerComponent />}
      />
    </React.Fragment>
  );
}

export default React.memo(DoneItemList);
